import { Lock } from 'lucide-react';

import type { Task } from '@/types/task';

interface QuickCompleteCheckboxProps {
  task: Task;
  onComplete: (id: string) => Promise<void>;
  size?: 'sm' | 'md';
  onError?: (message: string) => void;
}

const VISUAL_SIZE_SM = 20;
const VISUAL_SIZE_MD = 24;

export function QuickCompleteCheckbox({
  task,
  onComplete,
  size = 'md',
  onError,
}: QuickCompleteCheckboxProps) {
  const visualSize = size === 'sm' ? VISUAL_SIZE_SM : VISUAL_SIZE_MD;
  const isBlocked = task.blocked === true;
  const isCompleted = task.completed;

  async function activate(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    if (isBlocked) {
      onError?.('Complete prerequisite task first');
      return;
    }
    try {
      await onComplete(task.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to complete task';
      onError?.(message);
    }
  }

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    void activate(e);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void activate(e);
    }
  }

  const ariaLabel = isBlocked
    ? 'Blocked by prerequisite'
    : `Mark '${task.title}' complete`;

  return (
    <div
      role="checkbox"
      aria-checked={isCompleted}
      aria-disabled={isBlocked}
      aria-label={ariaLabel}
      tabIndex={isBlocked ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid="quick-complete-checkbox"
      className="flex items-center justify-center min-w-[44px] min-h-[44px] cursor-pointer flex-shrink-0"
    >
      {isBlocked ? (
        <Lock
          size={visualSize}
          className="text-bark-500"
          aria-hidden="true"
        />
      ) : isCompleted ? (
        <div
          className="rounded-full bg-[var(--color-accent)] flex items-center justify-center transition-opacity duration-[400ms]"
          style={{ width: visualSize, height: visualSize }}
          aria-hidden="true"
        >
          <svg
            width={visualSize * 0.55}
            height={visualSize * 0.45}
            viewBox="0 0 11 9"
            fill="none"
          >
            <path
              d="M1 4.5L4 7.5L10 1.5"
              stroke="#f0ede8"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      ) : (
        <div
          className="rounded-full border-2 border-bark-400 bg-transparent hover:border-[var(--color-accent)] transition-colors duration-100"
          style={{ width: visualSize, height: visualSize }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
